import { IUseWalletStore } from '@/src/store/global/types';
import { EMERGENCY_ADMIN_TOKEN_CS, FUND_STATUS_NOT_DEPLOYED, INVESTUNIT_MULTIPLIER } from '@/src/utils/constants';
import {
    Address,
    applyParamsToScript,
    Assets,
    Data,
    MintingPolicy,
    mintingPolicyToId,
    PolicyId,
    ScriptHash,
    sleep,
    UTxO,
    Validator,
    validatorToAddress,
    validatorToScriptHash,
} from '@lucid-evolution/lucid';
import {
    addAssetsList,
    explainErrorTx,
    find_UTxO_In_UTxOs,
    formatHash,
    FundAddScriptsTxParams,
    FundCreateParams,
    FundDeleteTxParams,
    FundDeployTxParams,
    FundDepositTxParams,
    FundFactory,
    FundHoldingCreateParams as FundHoldingCreateTxParams,
    FundMintFT_Mock_TxParams,
    FundReIndexingTxParams,
    FundUpdateParams,
    FundUpdateTxParams,
    FundWithdrawTxParams,
    isEmulator,
    isTokenADA,
    LUCID_NETWORK_MAINNET_NAME,
    LUCID_NETWORK_PREVIEW_NAME,
    optionsGetMinimal,
    pushSucessNotification,
    pushWarningNotification,
    showData,
    TX_PROPAGATION_DELAY_MS,
} from '../../Commons';
import { FundEntity } from '../../Entities/Fund.Entity';
import { FundHoldingEntity } from '../../Entities/Fund.Holding.Entity';
import { InvestUnitEntity } from '../../Entities/Invest.Unit.Entity';
import { ProtocolEntity } from '../../Entities/Protocol.Entity';
import { LucidToolsFrontEnd } from '../../lib';
import { InvestUnitApi } from '../InvestUnit/Invest.Unit.FrontEnd.Api.Calls';
import { ScriptApi } from '../Script/Script.FrontEnd.Api.Calls';
import { ScriptTxApiCalls } from '../Script/Script.FrontEnd.Tx.Api.Calls';
import { AddressToFollowApi } from '../index.exports';
import { SmartDBBtnHandlers } from '../index.exports.BtnHandlers.TxApiCalls';
import { FundApi } from './Fund.FrontEnd.Api.Calls';
import { FundTxApiCalls } from './Fund.FrontEnd.Tx.Api.Calls';

export class FundBtnHandlers extends SmartDBBtnHandlers {
    protected static _Entity = FundEntity;
    protected static _TxApiCalls = FundTxApiCalls;

    // #region class methods

    // #endregion class methods

    // #region front end Btn handlers

    public static async handleBtnFundCreate(
        walletStore: IUseWalletStore,
        protocol: ProtocolEntity,
        fundFactory: FundFactory,
        params: FundCreateParams
    ): Promise<string | undefined> {
        try {
            //--------------------------------------
            const lucid = await walletStore.getLucid();
            if (lucid === undefined) {
                throw 'Please connect your wallet';
            }
            //--------------------------------------
            // const lucidNetwork = lucid.network;
            //--------------------------------------
            if (walletStore.isWalletDataLoaded !== true) {
                throw 'Wallet Data is not ready';
            }
            //--------------------------------------
            const walletUTxOs = walletStore.getUTxOsAtWallet();
            if (walletUTxOs.length === 0) {
                throw 'You need at least one utxo to be used to mint Fund ID';
            }
            const uTxO = params.uTxO;
            if (find_UTxO_In_UTxOs(uTxO, walletUTxOs) === undefined) {
                throw 'uTxO is not in wallet';
            }
            //--------------------------------------
            console.log(`uTxO for creating Fund ID: ${uTxO}`);
            //--------------------------------------
            const fundPolicy_TxHash = uTxO.txHash;
            const fundPolicy_TxOutputIndex = uTxO.outputIndex;
            //--------------------------------------
            const protocolPolicyID_CS = protocol.pdpProtocolPolicyID_CS;
            //--------------------------------------
            const ParamsSchemaValidator = Data.Tuple([Data.Bytes(), Data.Bytes()]);
            type ParamsValidator = Data.Static<typeof ParamsSchemaValidator>;
            const ParamsValidator = ParamsSchemaValidator as unknown as ParamsValidator;
            //--------------------------------------
            const fundValidator_Params = { protocolPolicyID_CS, tokenEmergencyAdminPolicy_CS: EMERGENCY_ADMIN_TOKEN_CS };
            //--------------------------------------
            const fundValidator_Script: Validator = {
                type: 'PlutusV2',
                script: applyParamsToScript<ParamsValidator>(
                    fundFactory.ffdpFundValidator_PreScript.script,
                    [fundValidator_Params.protocolPolicyID_CS, fundValidator_Params.tokenEmergencyAdminPolicy_CS],
                    ParamsValidator
                ),
            };
            //--------------------------------------
            // lucid.network = LUCID_NETWORK_MAINNET_NAME;
            const fundValidator_Address_Mainnet: Address = validatorToAddress(LUCID_NETWORK_MAINNET_NAME, fundValidator_Script);
            console.log(`fundValidator_Address_Mainnet ${fundValidator_Address_Mainnet}`);
            //--------------------------------------
            // lucid.network = LUCID_NETWORK_PREVIEW_NAME;
            const fundValidator_Address_Testnet: Address = validatorToAddress(LUCID_NETWORK_PREVIEW_NAME, fundValidator_Script);
            console.log(`fundValidator_Address_Testnet ${fundValidator_Address_Testnet}`);
            //--------------------------------------
            const fundValidator_Hash: ScriptHash = validatorToScriptHash(fundValidator_Script);
            console.log(`fundValidator_Hash ${fundValidator_Hash}`);
            //--------------------------------------
            const ParamsSchemaPolicyID = Data.Tuple([Data.Bytes(), Data.Bytes(), Data.Integer(), Data.Bytes()]);
            type ParamsPolicyId = Data.Static<typeof ParamsSchemaPolicyID>;
            const ParamsPolicyId = ParamsSchemaPolicyID as unknown as ParamsPolicyId;
            //--------------------------------------
            const fundPolicy_Params = {
                protocolPolicyID_CS,
                fundPolicy_TxHash,
                fundPolicy_TxOutputIndex: BigInt(fundPolicy_TxOutputIndex),
                fundValidator_Hash,
            };
            //--------------------------------------
            const fundPolicy_Script: MintingPolicy = {
                type: 'PlutusV2',
                script: applyParamsToScript<ParamsPolicyId>(
                    fundFactory.ffdpFundPolicy_PreScript.script,
                    [fundPolicy_Params.protocolPolicyID_CS, fundPolicy_Params.fundPolicy_TxHash, fundPolicy_Params.fundPolicy_TxOutputIndex, fundPolicy_Params.fundValidator_Hash],
                    ParamsPolicyId
                ),
            };
            //--------------------------------------
            const fundPolicy_CS: PolicyId = mintingPolicyToId(fundPolicy_Script);
            console.log(`fundPolicy_CS ${fundPolicy_CS}`);
            //--------------------------------------
            const ParamsSchemaFundHoldingPolicyID = Data.Tuple([Data.Bytes()]);
            type ParamsFundHoldingPolicyId = Data.Static<typeof ParamsSchemaFundHoldingPolicyID>;
            const ParamsFundHoldingPolicyId = ParamsSchemaFundHoldingPolicyID as unknown as ParamsFundHoldingPolicyId;
            //--------------------------------------
            const fundHoldingPolicyID_Params = { fundPolicy_CS };
            //--------------------------------------
            const fundHoldingPolicyID_Script: MintingPolicy = {
                type: 'PlutusV2',
                script: applyParamsToScript<ParamsFundHoldingPolicyId>(
                    fundFactory.ffdpFundHoldingPolicyID_PreScript.script,
                    [fundHoldingPolicyID_Params.fundPolicy_CS],
                    ParamsFundHoldingPolicyId
                ),
            };
            //--------------------------------------
            const fundHoldingPolicyID_CS: PolicyId = mintingPolicyToId(fundHoldingPolicyID_Script);
            console.log(`fundHoldingPolicyID_CS ${fundHoldingPolicyID_CS}`);
            //--------------------------------------
            const ParamsSchemaFundHoldingValidator = Data.Tuple([Data.Bytes(), Data.Bytes(), Data.Bytes()]);
            type ParamsFundHoldingValidator = Data.Static<typeof ParamsSchemaFundHoldingValidator>;
            const ParamsFundHoldingValidator = ParamsSchemaFundHoldingValidator as unknown as ParamsFundHoldingValidator;
            //--------------------------------------
            const fundHoldingValidator_Params = { protocolPolicyID_CS, fundPolicy_CS, tokenEmergencyAdminPolicy_CS: EMERGENCY_ADMIN_TOKEN_CS };
            //--------------------------------------
            const fundHoldingValidator_Script: Validator = {
                type: 'PlutusV2',
                script: applyParamsToScript<ParamsFundHoldingValidator>(
                    fundFactory.ffdpFundHoldingValidator_PreScript.script,
                    [fundHoldingValidator_Params.protocolPolicyID_CS, fundHoldingValidator_Params.fundPolicy_CS, fundHoldingValidator_Params.tokenEmergencyAdminPolicy_CS],
                    ParamsFundHoldingValidator
                ),
            };
            //--------------------------------------
            // lucid.network = LUCID_NETWORK_MAINNET_NAME;
            const fundHoldingValidator_Address_Mainnet: Address = validatorToAddress(LUCID_NETWORK_MAINNET_NAME, fundHoldingValidator_Script);
            console.log(`fundHoldingValidator_Address_Mainnet ${fundHoldingValidator_Address_Mainnet}`);
            //--------------------------------------
            // lucid.network = LUCID_NETWORK_PREVIEW_NAME;
            const fundHoldingValidator_Address_Testnet: Address = validatorToAddress(LUCID_NETWORK_PREVIEW_NAME, fundHoldingValidator_Script);
            console.log(`fundHoldingValidator_Address_Testnet ${fundHoldingValidator_Address_Testnet}`);
            //--------------------------------------
            const fundHoldingValidator_Hash: ScriptHash = validatorToScriptHash(fundHoldingValidator_Script);
            console.log(`fundHoldingValidator_Hash ${fundHoldingValidator_Hash}`);
            //--------------------------------------
            // lucid.network = lucidNetwork;
            //--------------------------------------
            const fund: FundEntity = new FundEntity({
                name: params.name,
                protocol_id: protocol._DB_id,
                protocolVersion: protocol.pdpProtocolVersion,
                fdpFundVersion: fundFactory.ffdpFundVersion,
                fdpFundPolicy_Params: fundPolicy_Params,
                fdpFundPolicy_Script: fundPolicy_Script,
                fdpFundPolicy_CS: fundPolicy_CS,
                fdpFundValidator_Params: fundValidator_Params,
                fdpFundValidator_Hash: fundValidator_Hash,
                fdpFundValidator_Script: fundValidator_Script,
                fdpFundValidator_AddressTestnet: fundValidator_Address_Testnet,
                fdpFundValidator_AddressMainnet: fundValidator_Address_Mainnet,
                fdpFundHoldingPolicyID_Params: fundHoldingPolicyID_Params,
                fdpFundHoldingPolicyID_Script: fundHoldingPolicyID_Script,
                fdpFundHoldingPolicyID_CS: fundHoldingPolicyID_CS,
                fdpFundHoldingValidator_Params: fundHoldingValidator_Params,
                fdpFundHoldingValidator_Hash: fundHoldingValidator_Hash,
                fdpFundHoldingValidator_Script: fundHoldingValidator_Script,
                fdpFundHoldingValidator_AddressTestnet: fundHoldingValidator_Address_Testnet,
                fdpFundHoldingValidator_AddressMainnet: fundHoldingValidator_Address_Mainnet,
                showInSite: true,
                showInHome: false,
                isOfficial: false,
                status: FUND_STATUS_NOT_DEPLOYED,
            });
            const fund_ = await FundApi.createApi<FundEntity>(fund);
            //--------------------------------------
            await FundApi.createHookApi(FundEntity, fund_.getNet_Address(), fund_.fdpFundPolicy_CS);
            await FundApi.createHookApi(FundHoldingEntity, fund_.getNet_FundHolding_Validator_Address(), fund_.fdpFundHoldingPolicyID_CS);
            await FundApi.createHookApi(InvestUnitEntity, protocol.getNet_InvestUnit_Validator_Address(), fund_.fdpFundPolicy_CS);
            //--------------------------------------
            pushSucessNotification(`${this._Entity.className()}`, `${this._Entity.className()} created: ${fund_.show()}`, false);
            return fund_._DB_id;
            //--------------------------------------
        } catch (error) {
            console.log(`[${this._Entity.className()}] - handleBtnCreate - Error: ${error}`);
            pushWarningNotification(`${this._Entity.className()}`, `Error saving ${this._Entity.className()}: ${error}`);
            return undefined;
        }
    }

    public static async handleBtnFundUpdate<T extends FundEntity>(fund: T, params: FundUpdateParams): Promise<boolean> {
        try {
            await FundApi.updateMeWithParamsApi(fund, params, { doCallbackAfterLoad: true });
            pushSucessNotification(`${this._Entity.className()}`, `${this._Entity.className()} updated: ${fund.show()}`, false);
            return true;
        } catch (error) {
            console.log(`[${this._Entity.className()}] - handleBtnUpdate - Error: ${error}`);
            pushWarningNotification(`${this._Entity.className()}`, `Error updating ${this._Entity.className()}: ${error}`);
            return false;
        }
    }

    public static async handleBtnFundDelete<T extends FundEntity>(fund: T): Promise<boolean> {
        try {
            await FundApi.deleteApi(fund);
            pushSucessNotification(`${this._Entity.className()}`, `${this._Entity.className()} deleted: ${fund.show()}`, false);
            return true;
        } catch (error) {
            console.log(`[${this._Entity.className()}] - handleBtnDelete - Error: ${error}`);
            pushWarningNotification(`${this._Entity.className()}`, `Error deleting ${this._Entity.className()}: ${error}`);
            return false;
        }
    }

    public static async handleBtnCreateFundHooks<T extends FundEntity>(protocol: ProtocolEntity, fund: T): Promise<boolean> {
        try {
            const addressesToFollowFund = await AddressToFollowApi.getByAddressApi(fund.getNet_Address(), fund.getNET_id_CS());
            const addressesToFollowFundHolding = await AddressToFollowApi.getByAddressApi(fund.getNet_FundHolding_Validator_Address(), fund.getNet_FundHoldingPolicyID_CS());
            const addressesToFollowInvestUnit = await AddressToFollowApi.getByAddressApi(protocol.getNet_InvestUnit_Validator_Address(), fund.getNET_id_CS());
            if (
                addressesToFollowFund &&
                addressesToFollowFund.length > 0 &&
                addressesToFollowFundHolding &&
                addressesToFollowFundHolding.length > 0 &&
                addressesToFollowInvestUnit &&
                addressesToFollowInvestUnit.length > 0
            ) {
                throw 'Webhooks already exists';
            } else {
                await FundApi.createHookApi(FundEntity, fund.getNet_Address(), fund.fdpFundPolicy_CS);
                await FundApi.createHookApi(FundHoldingEntity, fund.getNet_FundHolding_Validator_Address(), fund.fdpFundHoldingPolicyID_CS);
                await FundApi.createHookApi(InvestUnitEntity, protocol.getNet_InvestUnit_Validator_Address(), fund.fdpFundPolicy_CS);
                pushSucessNotification(`${this._Entity.className()}`, `Webhooks created`, false);
                return true;
            }
        } catch (error) {
            console.log(`[${this._Entity.className()}] - handleBtnCreateFundHooks - Error: ${error}`);
            pushWarningNotification(`${this._Entity.className()}`, `Error creating webhooks: ${error}`);
            return false;
        }
    }

    public static async handleBtnFundSync<T extends FundEntity>(protocol: ProtocolEntity, fund: T, force: boolean = false): Promise<boolean> {
        try {
            await FundApi.syncWithAddressApi(FundEntity, fund.getNet_Address(), fund.getNET_id_CS(), force);
            await FundApi.syncWithAddressApi(FundHoldingEntity, fund.getNet_FundHolding_Validator_Address(), fund.getNet_FundHoldingPolicyID_CS(), force);
            await FundApi.syncWithAddressApi(InvestUnitEntity, protocol.getNet_InvestUnit_Validator_Address(), fund.getNET_id_CS(), force);
            pushSucessNotification(`${this._Entity.className()}`, `${this._Entity.className()} syncronized`, false);
            return true;
        } catch (error) {
            console.log(`[${this._Entity.className()}] - handleBtnFundSync - Error: ${error}`);
            pushWarningNotification(`${this._Entity.className()}`, `Error syncronizing ${this._Entity.className()}: ${error}`);
            return false;
        }
    }

    // #endregion front end Btn handlers

    // #region front end Btn tx handlers

    public static async handleBtnFundDeployTx<T extends FundEntity>(
        setProcessingTxMessage: (value: string) => void,
        setProcessingTxHash: (value: string) => void,
        walletStore: IUseWalletStore,
        fund: T,
        txParams: FundDeployTxParams
    ): Promise<boolean> {
        try {
            //--------------------------------------
            setProcessingTxMessage('Deploying Fund...');
            //--------------------------------------
            const { lucid, emulatorDB, walletTxParams } = await LucidToolsFrontEnd.prepareLucidFrontEndForTx(walletStore);
            //--------------------------------------
            const { txCborHex } = await this._TxApiCalls.deployTxApi(walletTxParams, txParams);
            // console.log(`${this._Entity.className()}] - handleBtnFundDeployTx - txCborHex  ${txCborHex}`)
            //--------------------------------------
            setProcessingTxMessage('Transaction prepared, waiting for sign to submit...');
            //--------------------------------------
            const txHash = await LucidToolsFrontEnd.signAndSubmitTx(lucid, txCborHex, walletStore.info, emulatorDB, walletStore.swDoNotPromtForSigning);
            //--------------------------------------
            setProcessingTxMessage(`Transaction submitted, waiting for confirmation...`);
            setProcessingTxHash(txHash);
            //--------------------------------------
            if (await LucidToolsFrontEnd.awaitTx(lucid, txHash)) {
                console.log(`${this._Entity.className()}] - handleBtnFundDeployTx - waitForTxConfirmation - Tx confirmed - ${txHash}`);
                pushSucessNotification(`${this._Entity.className()} Deploy Tx`, txHash, true);
            } else {
                console.log(`${this._Entity.className()}] - handleBtnFundDeployTx - waitForTxConfirmation - Tx not confirmed - ${txHash}`);
                throw `Tx not confirmed`;
            }
            //--------------------------------------
            setProcessingTxMessage(`Syncronizing...`);
            //--------------------------------------
            await walletStore.loadWalletData();
            //--------------------------------------
            return true;
        } catch (error) {
            const error_explained = explainErrorTx(error);
            console.log(`[${this._Entity.className()}] - handleBtnFundDeployTx - Error: ${error_explained}`);
            pushWarningNotification(`${this._Entity.className()} Deploy Tx`, error_explained);
            setProcessingTxMessage(error_explained);
            return false;
        }
    }

    public static async handleBtnFundUpdateTx<T extends FundEntity>(
        setProcessingTxMessage: (value: string) => void,
        setProcessingTxHash: (value: string) => void,
        walletStore: IUseWalletStore,
        fund: T,
        txParams: FundUpdateTxParams
    ): Promise<boolean> {
        try {
            setProcessingTxMessage('Updating Fund...');
            //--------------------------------------
            const { lucid, emulatorDB, walletTxParams } = await LucidToolsFrontEnd.prepareLucidFrontEndForTx(walletStore);
            //--------------------------------------
            const { txCborHex } = await this._TxApiCalls.updateTxApi(walletTxParams, txParams);
            // console.log(`${this._Entity.className()}] - handleBtnProtocolUpdateTx - txCborHex  ${txCborHex}`);
            //--------------------------------------
            setProcessingTxMessage('Transaction prepared, waiting for sign to submit...');
            //--------------------------------------
            const txHash = await LucidToolsFrontEnd.signAndSubmitTx(lucid, txCborHex, walletStore.info, emulatorDB, walletStore.swDoNotPromtForSigning);
            //--------------------------------------
            setProcessingTxMessage(`Transaction submitted, waiting for confirmation...`);
            setProcessingTxHash(txHash);
            //--------------------------------------
            if (await LucidToolsFrontEnd.awaitTx(lucid, txHash)) {
                console.log(`${this._Entity.className()}] - handleBtnProtocolUpdateTx - waitForTxConfirmation - Tx confirmed - ${txHash}`);
                pushSucessNotification(`${this._Entity.className()} Update Tx`, txHash, true);
            } else {
                console.log(`${this._Entity.className()}] - handleBtnProtocolUpdateTx - waitForTxConfirmation - Tx not confirmed - ${txHash}`);
            }
            //--------------------------------------
            setProcessingTxMessage(`Syncronizing...`);
            //--------------------------------------
            await walletStore.loadWalletData();
            //--------------------------------------
            return true;
        } catch (error) {
            const error_explained = explainErrorTx(error);
            console.log(`[${this._Entity.className()}] - handleBtnFundUpdateTx - Error: ${error_explained}`);
            pushWarningNotification(`${this._Entity.className()} Update Tx`, error_explained);
            setProcessingTxMessage(error_explained);
            return false;
        }
    }

    public static async handleBtnFundAddScriptsTx<T extends FundEntity>(
        setProcessingTxMessage: (value: string) => void,
        setProcessingTxHash: (value: string) => void,
        walletStore: IUseWalletStore,
        fund: T,
        txParams: FundAddScriptsTxParams
    ): Promise<boolean> {
        try {
            //--------------------------------------
            setProcessingTxMessage('Adding Fund Scripts...');
            //--------------------------------------
            const { lucid, emulatorDB, walletTxParams } = await LucidToolsFrontEnd.prepareLucidFrontEndForTx(walletStore);
            //--------------------------------------
            const fundWithScripts: FundEntity | undefined = await FundApi.getByIdApi_(fund._DB_id, {
                ...optionsGetMinimal,
                fieldsForSelect: FundEntity.defaultFieldsAddScriptsTxScript,
            });
            if (fundWithScripts === undefined) {
                throw `Invalid fund id`;
            }
            //--------------------------------------
            const scriptsToAdd = [
                { hash: fundWithScripts.fdpFundPolicy_CS, script: fundWithScripts.fdpFundPolicy_Script },
                { hash: fundWithScripts.fdpFundValidator_Hash, script: fundWithScripts.fdpFundValidator_Script },
                { hash: fundWithScripts.fdpFundHoldingPolicyID_CS, script: fundWithScripts.fdpFundHoldingPolicyID_Script },
                { hash: fundWithScripts.fdpFundHoldingValidator_Hash, script: fundWithScripts.fdpFundHoldingValidator_Script },
            ];
            //--------------------------------------
            const protocol = await FundApi.getByIdApi<ProtocolEntity>(ProtocolEntity, fund.protocol_id);
            if (protocol === undefined) {
                throw `Invalid protocol id`;
            }
            //--------------------------------------
            for (let index = 0; index < scriptsToAdd.length; index++) {
                //--------------------------------------
                const scriptToAdd = scriptsToAdd[index];
                //--------------------------------------
                const script_Hash: ScriptHash = scriptToAdd.hash;
                //--------------------------------------
                console.log(`${this._Entity.className()}] - handleBtnFundAddScriptsTx - Adding Script: ${script_Hash}`);
                //--------------------------------------
                const script = await ScriptApi.getByHashApi(script_Hash);
                if (script !== undefined) {
                    console.log(`${this._Entity.className()}] - handleBtnFundAddScriptsTx - Skipping script: ${script_Hash}, it's already added`);
                } else {
                    setProcessingTxMessage(`Script (${index + 1}/${scriptsToAdd.length}): Adding ${formatHash(script_Hash)}...`);
                    //--------------------------------------
                    const { txCborHex } = await ScriptTxApiCalls.addFundScriptTxApi(walletTxParams, { script_Hash, ...txParams });
                    //--------------------------------------
                    setProcessingTxMessage(`Script (${index + 1}/${scriptsToAdd.length}): Transaction prepared, waiting for sign to submit...`);
                    //--------------------------------------
                    const txHash = await LucidToolsFrontEnd.signAndSubmitTx(lucid, txCborHex, walletStore.info, emulatorDB, walletStore.swDoNotPromtForSigning);
                    //--------------------------------------
                    setProcessingTxMessage(`Script (${index + 1}/${scriptsToAdd.length}): Transaction submitted, waiting for confirmation...`);
                    setProcessingTxHash(txHash);
                    //--------------------------------------
                    if (await LucidToolsFrontEnd.awaitTx(lucid, txHash)) {
                        console.log(`${this._Entity.className()}] - handleBtnFundAddScriptsTx - waitForTxConfirmation - Tx confirmed - ${txHash}`);
                        //--------------------------------------
                        setProcessingTxMessage(`Script (${index + 1}/${scriptsToAdd.length}): Syncronizing...`);
                        //--------------------------------------
                        pushSucessNotification(`${this._Entity.className()} Add Script Tx`, txHash, true);
                    } else {
                        console.log(`${this._Entity.className()}] - handleBtnFundAddScriptsTx - waitForTxConfirmation - Tx not confirmed - ${txHash}`);
                        // throw `Tx not confirmed`
                    }
                    //--------------------------------------
                    if (!isEmulator) {
                        // como estoy haciendo mas de una tx y no quiero hacer todo el proceso de update wallet salvar emulador, si no hacerlo solo una vez al final,
                        // tengo que actualizar al menos las utxos de la wallet aqui de forma manual
                        // espero un poco para que se actualicen las utxos
                        await sleep(TX_PROPAGATION_DELAY_MS);
                    }
                    walletTxParams.utxos = await lucid.wallet().getUtxos();
                    //--------------------------------------
                }
            }
            //--------------------------------------
            await walletStore.loadWalletData();
            //--------------------------------------
            return true;
        } catch (error) {
            const error_explained = explainErrorTx(error);
            console.log(`[${this._Entity.className()}] - handleBtnProtocolAddScriptsTx - Error: ${error_explained}`);
            pushWarningNotification(`${this._Entity.className()} Add Scripts Tx`, error_explained);
            setProcessingTxMessage(error_explained);
            return false;
        }
    }

    public static async handleBtnFundAddFundHoldingTx<T extends FundEntity>(
        setProcessingTxMessage: (value: string) => void,
        setProcessingTxHash: (value: string) => void,
        walletStore: IUseWalletStore,
        fund: T,
        txParams: FundHoldingCreateTxParams
    ): Promise<boolean> {
        try {
            //--------------------------------------
            setProcessingTxMessage('Adding Fund Holding...');
            //--------------------------------------
            const { lucid, emulatorDB, walletTxParams } = await LucidToolsFrontEnd.prepareLucidFrontEndForTx(walletStore);
            //--------------------------------------
            const { txCborHex } = await this._TxApiCalls.addFundHoldingTxApi(walletTxParams, txParams);
            // console.log(`${this._Entity.className()}] - handleBtnProtocolAddFundFactoryTx - txCborHex  ${txCborHex}`)
            //--------------------------------------
            setProcessingTxMessage('Transaction prepared, waiting for sign to submit...');
            //--------------------------------------
            const txHash = await LucidToolsFrontEnd.signAndSubmitTx(lucid, txCborHex, walletStore.info, emulatorDB, walletStore.swDoNotPromtForSigning);
            //--------------------------------------
            setProcessingTxMessage(`Transaction submitted, waiting for confirmation...`);
            setProcessingTxHash(txHash);
            //--------------------------------------
            if (await LucidToolsFrontEnd.awaitTx(lucid, txHash)) {
                console.log(`${this._Entity.className()}] - handleBtnFundAddFundHoldingTx - waitForTxConfirmation - Tx confirmed - ${txHash}`);
                pushSucessNotification(`${this._Entity.className()} Add Holding Tx`, txHash, true);
            } else {
                console.log(`${this._Entity.className()}] - handleBtnFundAddFundHoldingTx - waitForTxConfirmation - Tx not confirmed - ${txHash}`);
                // throw `Tx not confirmed`
            }
            //--------------------------------------
            setProcessingTxMessage(`Syncronizing...`);
            //--------------------------------------
            await walletStore.loadWalletData();
            //--------------------------------------
            return true;
        } catch (error) {
            const error_explained = explainErrorTx(error);
            console.log(`[${this._Entity.className()}] - handleBtnProtocolAddFundFactoryTx - Error: ${error_explained}`);
            pushWarningNotification(`${this._Entity.className()} Add Holding Tx`, error_explained);
            setProcessingTxMessage(error_explained);
            return false;
        }
    }

    public static async handleBtnMintFundTokensTx<T extends FundEntity>(
        setProcessingTxMessage: (value: string) => void,
        setProcessingTxHash: (value: string) => void,
        walletStore: IUseWalletStore,
        protocol: ProtocolEntity,
        fund: T,
        txParams: FundMintFT_Mock_TxParams
    ): Promise<boolean> {
        try {
            //--------------------------------------
            setProcessingTxMessage('Minting Fund Tokens...');
            //--------------------------------------
            const { lucid, emulatorDB, walletTxParams } = await LucidToolsFrontEnd.prepareLucidFrontEndForTx(walletStore);
            //--------------------------------------
            const investUnit: InvestUnitEntity | undefined = await InvestUnitApi.getByFundPolicy_CSApi(fund.fdpFundPolicy_CS);
            if (investUnit === undefined) {
                throw `Invalid investUnit id`;
            }
            //--------------------------------------
            const amount = txParams.amount;
            //--------------------------------------
            const investUnitTokens = investUnit.iudInvestUnit.iuValues;
            //--------------------------------------
            const valueOf_TokensForMint: Assets = Object.fromEntries(
                investUnitTokens.map(([cs, tn, amount_]) => {
                    //-------------------
                    let AC_lucid = '';
                    if (isTokenADA(cs, tn)) {
                        AC_lucid = 'lovelace';
                    } else {
                        AC_lucid = cs + tn;
                    }
                    //-------------------
                    const requiredAmountDecimals = Number(amount_) / INVESTUNIT_MULTIPLIER; // invest unit is mulplied by 100
                    const totalAmountDecimals = Number(amount) * requiredAmountDecimals;
                    //-------------------
                    return [AC_lucid, BigInt(totalAmountDecimals)];
                })
            );
            const valueOf_TokensForMint2: Assets = Object.fromEntries(
                investUnitTokens.map(([cs, tn, amount_]) => {
                    //-------------------
                    let AC_lucid = '';
                    if (isTokenADA(cs, tn)) {
                        AC_lucid = 'lovelace';
                    } else {
                        AC_lucid = cs + tn + '31';
                    }
                    //-------------------
                    const requiredAmountDecimals = Number(amount_) / INVESTUNIT_MULTIPLIER; // invest unit is mulplied by 100
                    const totalAmountDecimals = Number(amount) * requiredAmountDecimals;
                    //-------------------
                    return [AC_lucid, BigInt(totalAmountDecimals)];
                })
            );
            console.log(`${this._Entity.className()}] - Minting Tx - valueOf_TokensForMint: ${showData(valueOf_TokensForMint)}`);
            console.log(`${this._Entity.className()}] - Minting Tx - valueOf_TokensForMint2: ${showData(valueOf_TokensForMint2)}`);
            //--------------------------------------
            const uTxOsAtWallet: UTxO[] = walletTxParams.utxos;
            if (uTxOsAtWallet.length === 0) {
                throw `Invalid uTxOsAtWallet length = 0`;
            }
            //--------------------------------------
            const uTxO: UTxO = uTxOsAtWallet[0];
            //--------------------------------------
            const assets: Assets = ((lucid.config().provider as any).ledger[uTxO.txHash + uTxO.outputIndex].utxo as UTxO).assets as Assets;
            ((lucid.config().provider as any).ledger[uTxO.txHash + uTxO.outputIndex].utxo as UTxO).assets = addAssetsList([assets, valueOf_TokensForMint, valueOf_TokensForMint2]);
            //--------------------------------------
            pushSucessNotification(`${this._Entity.className()} Minting Tx`, 'aaff', true);
            //--------------------------------------
            setProcessingTxMessage(`Syncronizing...`);
            //--------------------------------------
            if (isEmulator && emulatorDB !== undefined) {
                // normalmente esto se hace en el submit, pero esta tx es mock y no hay submit
                await LucidToolsFrontEnd.syncEmulatorAfterTx(lucid, emulatorDB);
            }
            //--------------------------------------
            await walletStore.loadWalletData({ swLoadParticipation: true });
            //--------------------------------------
            return true;
        } catch (error) {
            const error_explained = explainErrorTx(error);
            console.log(`[${this._Entity.className()}] - handleBtnFundMintTx - Error: ${error_explained}`);
            pushWarningNotification(`${this._Entity.className()} Minting Tx`, error_explained);
            setProcessingTxMessage(error_explained);
            return false;
        }
    }

    public static async handleBtnFundDepositTx<T extends FundEntity>(
        setProcessingTxMessage: (value: string) => void,
        setProcessingTxHash: (value: string) => void,
        walletStore: IUseWalletStore,
        protocol: ProtocolEntity,
        fund: T,
        txParams: FundDepositTxParams
    ): Promise<boolean> {
        try {
            //--------------------------------------
            setProcessingTxMessage('Fund Deposit...');
            //--------------------------------------
            const { lucid, emulatorDB, walletTxParams } = await LucidToolsFrontEnd.prepareLucidFrontEndForTx(walletStore);
            //--------------------------------------
            const { txCborHex } = await this._TxApiCalls.depositTxApi(walletTxParams, txParams);
            // console.log(`${this._Entity.className()}] - handleBtnFundDeployTx - txCborHex  ${txCborHex}`)
            //--------------------------------------
            setProcessingTxMessage('Transaction prepared, waiting for sign to submit...');
            //--------------------------------------
            const txHash = await LucidToolsFrontEnd.signAndSubmitTx(lucid, txCborHex, walletStore.info, emulatorDB, walletStore.swDoNotPromtForSigning);
            //--------------------------------------
            setProcessingTxMessage(`Transaction submitted, waiting for confirmation...`);
            setProcessingTxHash(txHash);
            //--------------------------------------
            if (await LucidToolsFrontEnd.awaitTx(lucid, txHash)) {
                console.log(`${this._Entity.className()}] - Deposit Tx - waitForTxConfirmation - Tx confirmed - ${txHash}`);
                pushSucessNotification(`${this._Entity.className()} Deposit Tx`, txHash, true);
            } else {
                console.log(`${this._Entity.className()}] - Deposit Tx - waitForTxConfirmation - Tx not confirmed - ${txHash}`);
                throw `Tx not confirmed`;
            }
            //--------------------------------------
            setProcessingTxMessage(`Syncronizing...`);
            //--------------------------------------
            await walletStore.loadWalletData({ swLoadParticipation: true });
            //--------------------------------------
            return true;
        } catch (error) {
            const error_explained = explainErrorTx(error);
            console.log(`[${this._Entity.className()}] - handleBtnFundDepositTx - Error: ${error_explained}`);
            pushWarningNotification(`${this._Entity.className()} Deposit Tx`, error_explained);
            setProcessingTxMessage(error_explained);
            return false;
        }
    }

    public static async handleBtnFundWithdrawTx<T extends FundEntity>(
        setProcessingTxMessage: (value: string) => void,
        setProcessingTxHash: (value: string) => void,
        walletStore: IUseWalletStore,
        protocol: ProtocolEntity,
        fund: T,
        txParams: FundWithdrawTxParams
    ): Promise<boolean> {
        try {
            //--------------------------------------
            setProcessingTxMessage('Fund Withdraw...');
            //--------------------------------------
            const { lucid, emulatorDB, walletTxParams } = await LucidToolsFrontEnd.prepareLucidFrontEndForTx(walletStore);
            //--------------------------------------
            const { txCborHex } = await this._TxApiCalls.withdrawTxApi(walletTxParams, txParams);
            // console.log(`${this._Entity.className()}] - handleBtnFundDeployTx - txCborHex  ${txCborHex}`)
            //--------------------------------------
            setProcessingTxMessage('Transaction prepared, waiting for sign to submit...');
            //--------------------------------------
            const txHash = await LucidToolsFrontEnd.signAndSubmitTx(lucid, txCborHex, walletStore.info, emulatorDB, walletStore.swDoNotPromtForSigning);
            //--------------------------------------
            setProcessingTxMessage(`Transaction submitted, waiting for confirmation...`);
            setProcessingTxHash(txHash);
            //--------------------------------------
            if (await LucidToolsFrontEnd.awaitTx(lucid, txHash)) {
                console.log(`${this._Entity.className()}] - Withdraw Tx - waitForTxConfirmation - Tx confirmed - ${txHash}`);
                pushSucessNotification(`${this._Entity.className()} Withdraw Tx`, txHash, true);
            } else {
                console.log(`${this._Entity.className()}] - Withdraw Tx - waitForTxConfirmation - Tx not confirmed - ${txHash}`);
                throw `Tx not confirmed`;
            }
            //--------------------------------------
            setProcessingTxMessage(`Syncronizing...`);
            //--------------------------------------
            await walletStore.loadWalletData({ swLoadParticipation: true });
            //--------------------------------------
            return true;
        } catch (error) {
            const error_explained = explainErrorTx(error);
            console.log(`[${this._Entity.className()}] - handleBtnFundWithdrawTx - Error: ${error_explained}`);
            pushWarningNotification(`${this._Entity.className()} Withdraw Tx`, error_explained);
            setProcessingTxMessage(error_explained);
            return false;
        }
    }

    public static async handleBtnFundReIndexingTx<T extends FundEntity>(
        setProcessingTxMessage: (value: string) => void,
        setProcessingTxHash: (value: string) => void,
        walletStore: IUseWalletStore,
        fund: T,
        txParams: FundReIndexingTxParams
    ): Promise<boolean> {
        try {
            //--------------------------------------
            setProcessingTxMessage('Fund Re Indexing...');
            //--------------------------------------
            const { lucid, emulatorDB, walletTxParams } = await LucidToolsFrontEnd.prepareLucidFrontEndForTx(walletStore);
            //--------------------------------------
            const { txCborHex } = await this._TxApiCalls.reIndexingTxApi(walletTxParams, txParams);
            // console.log(`${this._Entity.className()}] - handleBtnFundReIndexingTx - txCborHex  ${txCborHex}`)
            //--------------------------------------
            setProcessingTxMessage('Transaction prepared, waiting for sign to submit...');
            //--------------------------------------
            const txHash = await LucidToolsFrontEnd.signAndSubmitTx(lucid, txCborHex, walletStore.info, emulatorDB, walletStore.swDoNotPromtForSigning);
            //--------------------------------------
            setProcessingTxMessage(`Transaction submitted, waiting for confirmation...`);
            setProcessingTxHash(txHash);
            //--------------------------------------
            if (await LucidToolsFrontEnd.awaitTx(lucid, txHash)) {
                console.log(`${this._Entity.className()}] - handleBtnFundReIndexingTx - waitForTxConfirmation - Tx confirmed - ${txHash}`);
                pushSucessNotification(`${this._Entity.className()} Re Indexing Tx`, txHash, true);
            } else {
                console.log(`${this._Entity.className()}] - handleBtnFundReIndexingTx - waitForTxConfirmation - Tx not confirmed - ${txHash}`);
                throw `Tx not confirmed`;
            }
            //--------------------------------------
            setProcessingTxMessage(`Syncronizing...`);
            //--------------------------------------
            await walletStore.loadWalletData({ swLoadParticipation: true });
            //--------------------------------------
            return true;
        } catch (error) {
            const error_explained = explainErrorTx(error);
            console.log(`[${this._Entity.className()}] - handleBtnFundReIndexingTx - Error: ${error_explained}`);
            pushWarningNotification(`${this._Entity.className()} Re Indexing Tx`, error_explained);
            setProcessingTxMessage(error_explained);
            return false;
        }
    }

    public static async handleBtnFundDeleteTx<T extends FundEntity>(fund: T, txParams: FundDeleteTxParams): Promise<boolean> {
        try {
            // const txPrepared: TxPrepared = await FundActions.deleteTxApi(txParams);
            // // sign
            // // Submit
            // // waitForTxConfirmation
            // // sync
            // console.log(`${this._Entity.className()}] - handleBtnDeleteTx - txPrepared: ${log(txPrepared)}`);
            // //--------------------------------------
            // pushSucessNotification(`${this._Entity.className()} Delete Tx`, txPrepared.txHash, true);
            //--------------------------------------
            return true;
        } catch (error) {
            const error_explained = explainErrorTx(error);
            console.log(`[${this._Entity.className()}] - handleBtnDeleteTx - Error: ${error_explained}`);
            pushWarningNotification(`${this._Entity.className()} Delete Tx`, error_explained);
            // setProcessingTxMessage(error_explained);
            return false;
        }
    }

    // #endregion front end Btn tx handlers
}
